import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, History, Monitor } from 'lucide-react';
import { ACTION_VARIANT, fieldLabel, ValueChange } from '../components/RecordHistory';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, DetailItem, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar, SearchInput } from '../components/ui/FilterBar';
import { Input, Select } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Tabs } from '../components/ui/Tabs';
import { useAuditActions, useAuditDevices, useAuditLogs, useAuditTables, useUsers } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import { auditApi, type AuditFilters } from '../services/api';
import type { AuditDevice, AuditLog } from '../types';
import { ACTION_LABELS, formatDateTime, formatNumber, TABLE_LABELS } from '../utils/helpers';
import { t } from '../i18n';

const RECORD_LINKS: Partial<Record<string, (id: number) => string>> = {
  cars: (id) => `/cars/${id}`,
};

function DeviceCell({ ip, hostname, mac }: { ip: string | null; hostname: string | null; mac: string | null }) {
  if (!ip && !hostname && !mac) return <span className="text-secondary-400">–</span>;
  return (
    <div className="min-w-[9rem] text-xs leading-snug">
      {hostname && <p className="font-medium text-secondary-800">{hostname}</p>}
      {ip && <p className="font-mono text-secondary-600">{ip}</p>}
      {mac && <p className="font-mono text-secondary-500">{mac}</p>}
    </div>
  );
}

export function Audit() {
  const [filters, setFilters] = useUrlFilters({
    tab: 'changes',
    table_name: '',
    record_id: '',
    user_id: '',
    action: '',
    field_name: '',
    device: '',
    date_from: '',
    date_to: '',
    page: '1',
    page_size: '50',
  });
  const [detail, setDetail] = useState<AuditLog | null>(null);
  const page = toInt(filters.page, 1)!;
  const pageSize = toInt(filters.page_size, 50)!;

  const apiFilters: AuditFilters = {
    table_name: filters.table_name,
    record_id: toInt(filters.record_id),
    user_id: toInt(filters.user_id),
    action: filters.action,
    field_name: filters.field_name,
    device: filters.device,
    date_from: filters.date_from,
    date_to: filters.date_to,
  };

  const { data, isLoading, isFetching } = useAuditLogs({ ...apiFilters, page, page_size: pageSize });
  const { data: devices, isLoading: devicesLoading } = useAuditDevices(apiFilters, filters.tab === 'devices');
  const { data: tables } = useAuditTables();
  const { data: actions } = useAuditActions();
  const { data: users } = useUsers({ page_size: 500 });

  const columns: Column<AuditLog>[] = [
    { key: 'time', header: t('Čas'), className: 'whitespace-nowrap', render: (l) => formatDateTime(l.timestamp) },
    {
      key: 'user',
      header: t('Používateľ'),
      render: (l) => (l.user ? <span className="font-medium">{l.user.username}</span> : <span className="text-secondary-400">–</span>),
    },
    { key: 'action', header: t('Akcia'), render: (l) => <Badge variant={ACTION_VARIANT[l.action] ?? 'default'}>{ACTION_LABELS[l.action] ?? l.action}</Badge> },
    {
      key: 'record',
      header: t('Záznam'),
      className: 'whitespace-nowrap',
      render: (l) => {
        const label = `${TABLE_LABELS[l.table_name] ?? l.table_name} #${l.record_id}`;
        const link = RECORD_LINKS[l.table_name];
        return link && l.record_id ? (
          <Link to={link(l.record_id)} className="text-primary-700 hover:underline" onClick={(e) => e.stopPropagation()}>
            {label}
          </Link>
        ) : (
          label
        );
      },
    },
    {
      key: 'change',
      header: t('Zmena'),
      render: (l) => (
        <div className="max-w-sm truncate text-sm">
          <ValueChange log={l} />
        </div>
      ),
    },
    {
      key: 'device',
      header: t('Počítač / IP / MAC'),
      render: (l) => (
        <button
          type="button"
          className="text-left hover:underline"
          title={t('Zobraziť všetky zmeny z tohto zariadenia')}
          onClick={(e) => {
            e.stopPropagation();
            setFilters({ device: l.mac_address || l.ip_address || '' });
          }}
        >
          <DeviceCell ip={l.ip_address} hostname={l.hostname} mac={l.mac_address} />
        </button>
      ),
    },
  ];

  const deviceColumns: Column<AuditDevice>[] = [
    { key: 'device', header: t('Počítač / IP / MAC'), render: (d) => <DeviceCell ip={d.ip_address} hostname={d.hostname} mac={d.mac_address} /> },
    {
      key: 'users',
      header: t('Použité účty'),
      render: (d) =>
        d.usernames.length ? (
          <div className="flex flex-wrap gap-1">
            {d.usernames.map((u) => (
              <Badge key={u} variant={d.usernames.length > 1 ? 'warning' : 'default'}>
                {u}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-secondary-400">{t('len neúspešné prihlásenia')}</span>
        ),
    },
    { key: 'events', header: t('Záznamov'), className: 'text-right tabular-nums', headerClassName: 'text-right', render: (d) => formatNumber(d.events) },
    { key: 'first', header: t('Prvý záznam'), className: 'whitespace-nowrap', render: (d) => formatDateTime(d.first_seen) },
    { key: 'last', header: t('Posledný záznam'), className: 'whitespace-nowrap', render: (d) => formatDateTime(d.last_seen) },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right',
      render: (d) => (
        <Button size="sm" variant="ghost" onClick={() => setFilters({ tab: 'changes', device: d.mac_address || d.ip_address })}>
          {t('Zmeny')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('Audit log')}
        subtitle={t('Kto, kedy, z ktorého počítača a čo zmenil – na úrovni jednotlivých polí')}
        actions={
          <a href={auditApi.exportUrl(apiFilters)} download>
            <Button variant="outline">
              <Download className="h-4 w-4" />  {t('Export do CSV')}
            </Button>
          </a>
        }
      />
      <Tabs
        className="mb-4"
        value={filters.tab}
        onChange={(tab) => setFilters({ tab })}
        tabs={[
          { value: 'changes', label: t('Zmeny'), count: data?.total },
          { value: 'devices', label: t('Zariadenia') },
        ]}
      />
      <Card padding="none">
        <FilterBar>
          <SearchInput
            value={filters.device}
            onChange={(device) => setFilters({ device })}
            placeholder={t('Počítač, IP alebo MAC adresa…')}
          />
          <Select
            label={t('Používateľ')}
            wrapperClassName="sm:w-44"
            placeholder={t('Všetci')}
            value={filters.user_id}
            onChange={(e) => setFilters({ user_id: e.target.value })}
            options={(users?.users ?? []).map((u) => ({ value: String(u.id), label: u.username }))}
          />
          <Select
            label={t('Oblasť')}
            wrapperClassName="sm:w-40"
            placeholder={t('Všetko')}
            value={filters.table_name}
            onChange={(e) => setFilters({ table_name: e.target.value })}
            options={(tables?.tables ?? []).map((t) => ({ value: t, label: TABLE_LABELS[t] ?? t }))}
          />
          <Select
            label={t('Akcia')}
            wrapperClassName="sm:w-44"
            placeholder={t('Všetky')}
            value={filters.action}
            onChange={(e) => setFilters({ action: e.target.value })}
            options={(actions?.actions ?? []).map((a) => ({ value: a, label: ACTION_LABELS[a] ?? a }))}
          />
          {filters.tab === 'changes' && (
            <>
              <Input
                label={t('ID záznamu')}
                type="number"
                min={0}
                wrapperClassName="sm:w-28"
                value={filters.record_id}
                onChange={(e) => setFilters({ record_id: e.target.value })}
              />
              <Input label={t('Pole')} wrapperClassName="sm:w-36" placeholder={t('napr. current_km')} value={filters.field_name} onChange={(e) => setFilters({ field_name: e.target.value })} />
            </>
          )}
          <Input label={t('Od')} type="date" wrapperClassName="sm:w-40" value={filters.date_from} onChange={(e) => setFilters({ date_from: e.target.value })} />
          <Input label={t('Do')} type="date" wrapperClassName="sm:w-40" value={filters.date_to} onChange={(e) => setFilters({ date_to: e.target.value })} />
        </FilterBar>

        {filters.tab === 'devices' ? (
          <>
            <p className="border-b border-secondary-100 px-4 py-2 text-xs text-secondary-500">
              {t('Počítače, z ktorých sa v systéme niečo robilo. Zvýraznené sú zariadenia, z ktorých sa prihlásilo viac účtov. MAC adresu server zistí len pri počítačoch v tej istej sieti (VLAN) ako server.')}
            </p>
            <DataTable
              data={devices}
              columns={deviceColumns}
              rowKey={(d) => `${d.ip_address}|${d.hostname}|${d.mac_address}`}
              isLoading={devicesLoading}
              emptyMessage={<EmptyState icon={<Monitor className="h-10 w-10" />} title={t('Žiadne zariadenia nezodpovedajú filtru')} />}
            />
          </>
        ) : (
          <DataTable
            data={data?.logs}
            columns={columns}
            rowKey={(l) => l.id}
            isLoading={isLoading}
            isFetching={isFetching}
            onRowClick={setDetail}
            rowClassName={(l) => (l.action === 'LOGIN_FAILED' ? 'bg-red-50/60' : undefined)}
            emptyMessage={<EmptyState icon={<History className="h-10 w-10" />} title={t('Žiadne záznamy nezodpovedajú filtru')} />}
            pagination={
              data && {
                page,
                pageSize,
                total: data.total,
                onPageChange: (p) => setFilters({ page: String(p) }),
                onPageSizeChange: (s) => setFilters({ page_size: String(s) }),
              }
            }
          />
        )}
      </Card>

      <Modal isOpen={!!detail} onClose={() => setDetail(null)} title={t('Detail zmeny')} size="lg">
        {detail && (
          <div className="space-y-5">
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <DetailItem label={t('Čas')}>{formatDateTime(detail.timestamp)}</DetailItem>
              <DetailItem label={t('Používateľ')}>
                {detail.user ? `${detail.user.username}${detail.user.full_name ? ` (${detail.user.full_name})` : ''}` : '–'}
              </DetailItem>
              <DetailItem label={t('Akcia')}>{ACTION_LABELS[detail.action] ?? detail.action}</DetailItem>
              <DetailItem label={t('Záznam')}>
                {TABLE_LABELS[detail.table_name] ?? detail.table_name} #{detail.record_id}
              </DetailItem>
              <DetailItem label={t('Pole')}>{detail.field_name ? `${fieldLabel(detail.field_name)} (${detail.field_name})` : '–'}</DetailItem>
            </dl>
            <div className="rounded-lg border border-secondary-200 p-4">
              <p className="mb-3 text-sm font-semibold text-secondary-900">{t('Zariadenie')}</p>
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <DetailItem label={t('IP adresa')}>
                  <span className="font-mono">{detail.ip_address ?? '–'}</span>
                </DetailItem>
                <DetailItem label={t('Názov počítača')}>{detail.hostname ?? '–'}</DetailItem>
                <DetailItem label={t('MAC adresa')}>
                  <span className="font-mono">{detail.mac_address ?? t('nezistená')}</span>
                </DetailItem>
                <DetailItem label={t('Odtlačok prehliadača')}>
                  <span className="font-mono">{detail.device_id ?? '–'}</span>
                </DetailItem>
              </dl>
              <p className="mt-3 break-words text-xs text-secondary-500">{detail.user_agent ?? ''}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-medium uppercase text-secondary-500">{t('Pôvodná hodnota')}</p>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-red-50 p-3 text-sm text-red-900">{detail.old_value ?? '∅'}</pre>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase text-secondary-500">{t('Nová hodnota')}</p>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-green-50 p-3 text-sm text-green-900">{detail.new_value ?? '∅'}</pre>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
